import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Typography,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import * as React from 'react';

// shown after the vacancy reference is copied, on both tabs
export const referralSteps =
  "On the candidate's Applications tab: Create application, Vacancy application, then paste it.";

const cardWidth = 340;
// rough card height, only used to decide above or below the highlighted part
const cardHeight = 200;
const gap = 18;

const useStyles = makeStyles((theme) => ({
  clickAway: {
    position: 'fixed',
    inset: 0,
    zIndex: 1300,
  },
  dimmed: {
    background: 'rgba(13, 18, 30, 0.68)',
  },
  // a hole in the dim layer: the huge shadow darkens everything around the part being explained
  spotlight: {
    position: 'fixed',
    zIndex: 1301,
    pointerEvents: 'none',
    borderRadius: theme.shape.borderRadius,
    boxShadow: `0 0 0 4px ${theme.palette.primary.main}, 0 0 0 9999px rgba(13, 18, 30, 0.68)`,
  },
  card: {
    position: 'fixed',
    zIndex: 1302,
    width: cardWidth,
    maxWidth: 'calc(100vw - 32px)',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[8],
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.75),
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.75),
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  list: {
    paddingLeft: theme.spacing(2.5),
    margin: 0,
    '& li': {
      marginBottom: theme.spacing(1),
    },
  },
}));

interface IntroProps {
  open: boolean;
  onDone(): void;
}

// recruiters skim, so the button stays greyed out for a few seconds
const readSeconds = 5;

// shown once per browser, for both tabs
export const MatcherIntroDialog: React.FC<IntroProps> = ({ open, onDone }) => {
  const classes = useStyles();
  const [secondsLeft, setSecondsLeft] = React.useState(readSeconds);

  React.useEffect(() => {
    if (!open || secondsLeft <= 0) return undefined;
    const handle = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(handle);
  }, [open, secondsLeft]);

  return (
    <Dialog
      open={open}
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Before you start</DialogTitle>
      <DialogContent>
        <Typography variant="body2" paragraph>
          This tool finds the best candidates for a vacancy, and the best
          vacancies for a candidate. It's still a work in progress, but early
          results look good.
        </Typography>
        <Typography variant="body2" paragraph>
          Good to know:
        </Typography>
        <Typography variant="body2" component="ul" className={classes.list}>
          <li>
            <strong>
              Updates to candidates and vacancies are synced periodically.
            </strong>{' '}
            This means a brand new candidate may not be available for a search
            until the following morning. Changes to vacancies (e.g. status,
            description) may take a few hours to show up.
          </li>
          <li>
            <strong>Location filters is by region, not by town.</strong>
          </li>
          <li>
            <strong>Scores are generated by AI.</strong> This means that the
            scores are somewhat arbitrary. The ranking isn't though, and you
            should still check out all results just in case.
          </li>
          <li>
            <strong>
              Results are only as good as what we have on the ATS.
            </strong>{' '}
            If there's a genuinely great candidate who is not showing up, speak
            to me (Nick).
          </li>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onDone}
          color="primary"
          variant="contained"
          disabled={secondsLeft > 0}
        >
          {secondsLeft > 0
            ? `Start searching (${secondsLeft})`
            : 'Start searching'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export interface GuideStep {
  title: string;
  body: string;
  // when the part isn't on screen (no results yet), the card sits in the middle instead
  target: React.RefObject<HTMLElement>;
}

interface TutorialProps {
  steps: GuideStep[];
  open: boolean;
  onClose(): void;
}

export const MatcherTutorial: React.FC<TutorialProps> = ({
  steps,
  open,
  onClose,
}) => {
  const classes = useStyles();
  const [stepIndex, setStepIndex] = React.useState(0);
  const [rect, setRect] = React.useState<DOMRect | null>(null);

  // every new run starts from the first step
  React.useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const el = steps[stepIndex].target.current;
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });

    // the smooth scroll moves the part, so follow it
    function updateRect() {
      setRect(el?.getBoundingClientRect() ?? null);
    }
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [open, stepIndex, steps]);

  if (!open) return null;

  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  // below the part if it fits, else above, else bottom right (results can be taller than the screen)
  let cardStyle: React.CSSProperties = {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };
  if (rect) {
    const left = Math.min(
      Math.max(16, rect.left),
      Math.max(16, window.innerWidth - cardWidth - 16),
    );
    if (rect.bottom + gap + cardHeight < window.innerHeight) {
      cardStyle = { top: rect.bottom + gap, left };
    } else if (rect.top - gap - cardHeight > 0) {
      cardStyle = { top: rect.top - gap - cardHeight, left };
    } else {
      cardStyle = { bottom: 16, right: 16 };
    }
  }

  return (
    <>
      <Box
        className={`${classes.clickAway}${rect ? '' : ` ${classes.dimmed}`}`}
        onClick={onClose}
      />
      {rect && (
        <Box
          className={classes.spotlight}
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
          }}
        />
      )}
      <Box
        className={classes.card}
        style={cardStyle}
        role="dialog"
        aria-live="polite"
        aria-label={step.title}
      >
        <Typography className={classes.eyebrow}>Quick guide</Typography>
        <Typography className={classes.title}>{step.title}</Typography>
        <Typography variant="body2" color="textSecondary">
          {step.body}
        </Typography>
        <Box className={classes.actions}>
          <Typography variant="caption" color="textSecondary">
            {stepIndex + 1} / {steps.length}
          </Typography>
          <Box>
            <Button
              onClick={() => setStepIndex((i) => i - 1)}
              disabled={stepIndex === 0}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
            <Button
              onClick={() => (isLast ? onClose() : setStepIndex((i) => i + 1))}
              color="primary"
              variant="contained"
              endIcon={isLast ? undefined : <ArrowForwardIcon />}
            >
              {isLast ? 'Done' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};
